import { useCallback, useEffect, useState } from "react";
import { mmss } from "../lib/durations";
import { startAlarm, stopAlarm, unlockAudio } from "../lib/alarm";

const scale = () => +window.__cookTimerScale || (/[?&]testtimer=1\b/.test(window.location.search) ? 1 / 60 : 1);
const left = (t, now) => (t.end ? Math.max(0, t.end - now) : t.left);

export function useTimers() {
  const [ts, setTs] = useState([]);
  const [ringing, setRinging] = useState([]);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!ts.some(t => t.end)) return;
    const h = () => {
      const now = Date.now();
      const due = ts.filter(t => t.end && t.end <= now);
      if (!due.length) return tick(v => v + 1);
      setTs(x => x.map(t => (due.some(d => d.id === t.id) ? { ...t, end: null, left: 0, done: true } : t)));
      setRinging(r => [...r, ...due.map(d => d.step)]);
      startAlarm(due.map(d => `Trin ${d.step}`).join(", "));
    };
    const iv = setInterval(h, 250);
    document.addEventListener("visibilitychange", h);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", h); };
  }, [ts]);
  useEffect(() => () => stopAlarm(), []);
  const upd = (id, f) => setTs(x => x.map(t => (t.id === id ? { ...t, ...f(t) } : t)));
  const start = useCallback((step, secs) => {
    unlockAudio();
    const id = `${step}-${secs}`, now = Date.now();
    setTs(x => {
      const t = x.find(y => y.id === id);
      if (t) return x.map(y => (y.id === id && !y.end && y.left > 0 ? { ...y, end: now + y.left } : y));
      const sc = scale(), full = secs * 1000 * sc;
      return [...x, { id, step, secs, sc, full, left: full, end: now + full, done: false }];
    });
  }, []);
  const api = {
    list: ts, ringing,
    get: id => ts.find(t => t.id === id),
    start,
    pause: id => upd(id, t => ({ left: left(t, Date.now()), end: null })),
    reset: id => upd(id, t => ({ left: t.full, end: null, done: false })),
    remove: id => setTs(x => x.filter(t => t.id !== id)),
    dismiss: () => { stopAlarm(); setRinging([]); },
  };
  return api;
}

export function TimerCtl({ t, timers, compact }) {
  const ms = left(t, Date.now()) / t.sc;
  const state = t.done ? "done" : t.end ? "running" : "paused";
  return (
    <div className={`cktimer ${state}${compact ? " compact" : ""}`} data-testid={`cook-timer-${compact ? "bar-" : ""}${t.id}`} data-state={state}>
      <span className="cklbl">Trin {t.step}</span>
      <b className="cktime" role="timer" aria-live="off" data-testid={`cook-timer-${compact ? "bar-" : ""}time-${t.id}`}>{mmss(ms)}</b>
      <span className="ckbtns">
        {t.end
          ? <button type="button" className="btn ghost" onClick={() => timers.pause(t.id)} data-testid={`cook-timer-${compact ? "bar-" : ""}pause-${t.id}`}>Pause</button>
          : !t.done && <button type="button" className="btn" onClick={() => timers.start(t.step, t.secs)} data-testid={`cook-timer-${compact ? "bar-" : ""}resume-${t.id}`}>{t.left === t.full ? "Start" : "Fortsæt"}</button>}
        <button type="button" className="btn ghost" onClick={() => timers.reset(t.id)} data-testid={`cook-timer-${compact ? "bar-" : ""}reset-${t.id}`}>Nulstil</button>
        {compact && <button type="button" className="btn ghost ckx" aria-label={`Fjern timer for trin ${t.step}`} onClick={() => timers.remove(t.id)} data-testid={`cook-timer-bar-remove-${t.id}`}>×</button>}
      </span>
    </div>
  );
}

export function TimerDock({ timers, floating }) {
  if (!timers.list.length && !timers.ringing.length) return null;
  return (
    <div className={`ckdock${floating ? " floating noprint" : ""}`} data-testid={floating ? "cook-dock-page" : "cook-dock"}>
      {timers.ringing.length > 0 && (
        <div className="ckalarm" role="alert" data-testid="cook-alarm">
          <b>Tiden er gået!</b> <span>{[...new Set(timers.ringing)].map(s => `Trin ${s}`).join(", ")}</span>
          <button type="button" className="btn" onClick={timers.dismiss} data-testid="cook-alarm-dismiss">OK</button>
        </div>
      )}
      {timers.list.length > 0 && (
        <div className="ckbar" aria-label="Timere" data-testid="cook-timer-bar">
          {timers.list.map(t => <TimerCtl key={t.id} t={t} timers={timers} compact />)}
        </div>
      )}
    </div>
  );
}
